import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, MapPin, Home, Bed, Maximize, Euro, Filter, X, Building2, Sparkles, Map, List } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import Header from "../components/Header";
import PropertyCard from "../components/PropertyCard";
import LocationSelector from "../components/LocationSelector";
import PropertyMapView from "../components/PropertyMapView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // "list" or "map"
  const [location, setLocation] = useState({
    judet: "",
    localitate: "",
    sector: "",
    zona: "",
    strada: ""
  });
  const [filters, setFilters] = useState({
    rooms: "",
    propertyType: "",
    minPrice: "",
    maxPrice: ""
  });

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (location.judet) params.append("judet", location.judet);
      if (location.localitate) params.append("localitate", location.localitate);
      if (location.sector) params.append("sector", location.sector);
      if (location.zona) params.append("zona", location.zona);
      if (filters.rooms) params.append("rooms", filters.rooms);
      if (filters.propertyType) params.append("property_type", filters.propertyType);
      if (filters.minPrice) params.append("min_price", filters.minPrice);
      if (filters.maxPrice) params.append("max_price", filters.maxPrice);
      
      const response = await axios.get(`${API}/listings?${params.toString()}`);
      setListings(response.data);
    } catch (error) {
      console.error("Error fetching listings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchListings();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setLocation({
      judet: "",
      localitate: "",
      sector: "",
      zona: "",
      strada: ""
    });
    setFilters({
      rooms: "",
      propertyType: "",
      minPrice: "",
      maxPrice: ""
    });
  };

  return (
    <div className="min-h-screen bg-stone-100">
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/17174767/pexels-photo-17174767.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/70 via-emerald-950/50 to-stone-100" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <h1 
            data-testid="hero-title"
            className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 animate-fade-in"
          >
            Găsește-ți Casa Visurilor
          </h1>
          <p className="text-base sm:text-lg font-medium text-amber-300 mb-4 tracking-wide animate-fade-in">
            Imobilul potrivit. Clientul potrivit. Creditul potrivit.
          </p>
          <p className="text-base sm:text-lg text-stone-200 mb-10 max-w-2xl mx-auto animate-fade-in stagger-1">
            Anunțuri imobiliare de la proprietari și agenții, cu toate opțiunile de creditare într-un singur loc
          </p>
          
          {/* Search Box */}
          <div 
            data-testid="search-box"
            className="bg-white/95 backdrop-blur-md rounded-2xl p-4 sm:p-6 shadow-xl animate-slide-up stagger-2"
          >
            <div className="flex flex-col gap-4">
              {/* Location Selector */}
              <LocationSelector 
                value={location}
                onChange={setLocation}
              />
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Select value={filters.rooms} onValueChange={(v) => setFilters({ ...filters, rooms: v })}>
                  <SelectTrigger data-testid="rooms-select" className="w-full sm:w-40 h-12 rounded-xl border-stone-200">
                    <SelectValue placeholder="Camere" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 cameră</SelectItem>
                    <SelectItem value="2">2 camere</SelectItem>
                    <SelectItem value="3">3 camere</SelectItem>
                    <SelectItem value="4">4 camere</SelectItem>
                    <SelectItem value="5">5+ camere</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={filters.propertyType} onValueChange={(v) => setFilters({ ...filters, propertyType: v })}>
                  <SelectTrigger data-testid="property-type-select" className="w-full sm:w-48 h-12 rounded-xl border-stone-200">
                    <SelectValue placeholder="Tip proprietate" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartament">Apartament</SelectItem>
                    <SelectItem value="casa">Casă</SelectItem>
                    <SelectItem value="teren">Teren</SelectItem>
                    <SelectItem value="spatiu_comercial">Spațiu comercial</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button 
                  data-testid="filter-button"
                  variant="outline" 
                  className="h-12 rounded-xl border-stone-200"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Mai multe filtre
                </Button>
                
                <Button 
                  data-testid="search-button"
                  onClick={handleSearch}
                  className="h-12 px-8 rounded-xl bg-emerald-950 hover:bg-emerald-900"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Caută
                </Button>
              </div>
            </div>
            
            {/* Expanded Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-stone-200 animate-fade-in">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="text-sm text-stone-600 mb-2 block">
                      Preț (EUR)
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <Input
                          data-testid="price-min-input"
                          type="number"
                          placeholder="Min (ex: 50000)"
                          value={filters.minPrice}
                          onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                          min={0}
                          max={4000000}
                          className="w-full"
                        />
                      </div>
                      <span className="text-stone-400">—</span>
                      <div className="flex-1">
                        <Input
                          data-testid="price-max-input"
                          type="number"
                          placeholder="Max (ex: 200000)"
                          value={filters.maxPrice}
                          onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                          min={0}
                          max={4000000}
                          className="w-full"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-stone-400 mt-1">Maxim 4.000.000 €</p>
                  </div>
                </div>
                
                <div className="flex justify-end mt-4">
                  <Button variant="ghost" onClick={clearFilters} className="text-stone-500">
                    <X className="w-4 h-4 mr-2" />
                    Resetează filtre
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <div className="bg-white rounded-2xl p-6 card-shadow flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 text-emerald-700" />
              </div>
              <div>
                <h3 className="font-['Outfit'] font-semibold text-lg mb-1">Anunțuri verificate</h3>
                <p className="text-stone-500 text-sm">Direct de la proprietari, fără agenții</p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-6 card-shadow flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Euro className="w-6 h-6 text-amber-700" />
              </div>
              <div>
                <h3 className="font-['Outfit'] font-semibold text-lg mb-1">Simulator credit</h3>
                <p className="text-stone-500 text-sm">Calculează rata lunară instant</p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl p-6 card-shadow flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <h3 className="font-['Outfit'] font-semibold text-lg mb-1">Descrieri AI</h3>
                <p className="text-stone-500 text-sm">Anunțuri profesionale generate automat</p>
              </div>
            </div>
          </div>
          
          {/* Listings Grid */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-stone-900">
              Proprietăți disponibile
            </h2>
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="text-stone-600">
                {listings.length} anunțuri
              </Badge>
              {/* View Mode Toggle */}
              <div className="flex items-center bg-stone-100 rounded-lg p-1">
                <Button
                  variant={viewMode === "list" ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className={`h-8 px-3 ${viewMode === "list" ? "bg-white shadow-sm" : ""}`}
                >
                  <List className="w-4 h-4 mr-1" />
                  Listă
                </Button>
                <Button
                  variant={viewMode === "map" ? "secondary" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("map")}
                  className={`h-8 px-3 ${viewMode === "map" ? "bg-white shadow-sm" : ""}`}
                >
                  <Map className="w-4 h-4 mr-1" />
                  Hartă
                </Button>
              </div>
            </div>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl h-80 animate-pulse" />
              ))}
            </div>
          ) : listings.length === 0 ? (
            <div data-testid="no-listings-message" className="text-center py-16">
              <Home className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-stone-700 mb-2">Nu am găsit proprietăți</h3>
              <p className="text-stone-500">Încearcă să modifici filtrele de căutare</p>
            </div>
          ) : viewMode === "map" ? (
            /* Map View */
            <div data-testid="listings-map" className="bg-white rounded-2xl card-shadow overflow-hidden">
              <PropertyMapView listings={listings} height="500px" />
            </div>
          ) : (
            /* List View */
            <div data-testid="listings-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((listing, index) => (
                <PropertyCard key={listing.id} listing={listing} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 px-4 bg-emerald-950">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-4">
            Ești proprietar sau agent imobiliar?
          </h2>
          <p className="text-emerald-100 text-lg mb-8">
            Publică anunțul tău gratuit și ajunge la cumpărători eligibili pentru credit
          </p>
          <Link to="/dashboard">
            <Button 
              data-testid="cta-owner-button"
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-8 py-6 text-lg font-semibold pulse-glow"
            >
              Publică anunț gratuit
            </Button>
          </Link>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-white py-8 px-4 border-t border-stone-200">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center">
            <img 
              src="/imofi_logo.png" 
              alt="Imofit" 
              className="h-12 w-auto object-contain"
            />
          </Link>
          <p className="text-stone-500 text-sm">
            © 2026 Imofit. Toate drepturile rezervate.
          </p>
          <div className="flex gap-4">
            <Link to="/admin" className="text-stone-500 hover:text-stone-700 text-sm">
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
